import React, { useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './ResetPasswordPage.css';

function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [notificationPref, setNotificationPref] = useState('whatsapp'); // Default to WhatsApp
    const [countryCode, setCountryCode] = useState('+62'); // Default to Indonesia
    const [phoneNumber, setPhoneNumber] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!token) {
            setError('Invalid reset link');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        // Validate WhatsApp number (now mandatory)
        if (!countryCode || !phoneNumber) {
            setError('WhatsApp number is required for booking notifications');
            return;
        }

        if (!countryCode.startsWith('+')) {
            setError('Country code must start with + (e.g., +62)');
            return;
        }

        if (phoneNumber.length < 8 || phoneNumber.length > 15) {
            setError('Phone number must be between 8 and 15 digits');
            return;
        }

        // Remove non-digit characters from phone number
        const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');

        setLoading(true);

        try {
            await axios.post('/api/auth/reset-password', {
                token,
                password,
                notification_preference: notificationPref,
                phone_country_code: countryCode,
                phone_number: cleanPhoneNumber,
            });
            setSuccess('Password reset successful! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Reset failed');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="reset-container">
                <div className="reset-box">
                    <img src="/assets/UNICEF_Logo.png" alt="UNICEF" className="reset-logo" />
                    <h1>Invalid Reset Link</h1>
                    <p>The reset link is invalid or has expired.</p>
                    <button onClick={() => navigate('/login')} className="btn-back-login">
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-container">
            <div className="reset-box">
                <img src="/assets/UNICEF_Logo.png" alt="UNICEF" className="reset-logo" />
                <h1>Reset Password</h1>
                <p className="subtitle">Enter your new password</p>

                {error && <div className="error">{error}</div>}
                {success && <div className="success">{success}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>New Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="••••••••"
                            minLength="6"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label>Confirm Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            placeholder="••••••••"
                            minLength="6"
                        />
                    </div>
                    <div className="form-group">
                        <label>Notification Preference</label>
                        <select value={notificationPref} onChange={(e) => setNotificationPref(e.target.value)}>
                            <option value="whatsapp">WhatsApp (Recommended)</option>
                            <option value="email">Email Only</option>
                        </select>
                        <span className="field-hint">Booking confirmations and reminders will be sent via your preferred channel</span>
                    </div>

                    <div className="form-group">
                        <label>WhatsApp Number (Required) *</label>
                        <p className="field-description">Enter your WhatsApp number to receive booking notifications</p>
                        <div className="phone-input-group">
                            <input
                                type="text"
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                placeholder="+62"
                                className="country-code-input"
                                required
                                maxLength="5"
                            />
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="81234567890"
                                className="phone-number-input"
                                required
                                pattern="[0-9]{8,15}"
                            />
                        </div>
                        <span className="field-hint">Format: +[country code] [phone number without 0]. Example: +62 81234567890</span>
                    </div>
                    <button type="submit" disabled={loading} className="btn-submit">
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>

                <button onClick={() => navigate('/login')} className="btn-back-login">
                    Back to Login
                </button>
            </div>
        </div>
    );
}

export default ResetPasswordPage;
